import CloseIcon from "@mui/icons-material/Close";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ReplayIcon from "@mui/icons-material/Replay";
import { Box, Button, ButtonBase, IconButton, Typography } from "@mui/material";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { compressImage } from "@/utils/compressImage";

type Props = {
	label: string;
	file: File | null;
	// Already-saved photo (edit): previewed until replaced or cleared.
	existingUrl?: string | null;
	disabled?: boolean;
	onChange: (file: File | null) => void;
};

export const PhotoCapture = ({
	label,
	file,
	existingUrl = null,
	disabled = false,
	onChange,
}: Props) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	useEffect(() => {
		if (!file) {
			setPreviewUrl(existingUrl);
			return;
		}
		const url = URL.createObjectURL(file);
		setPreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [file, existingUrl]);

	const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.files?.[0] ?? null;
		// Reset so re-selecting the same file fires onChange again
		e.target.value = "";
		onChange(raw ? await compressImage(raw) : null);
	};

	const openCamera = () => inputRef.current?.click();

	return (
		<Box>
			<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
				{label}
			</Typography>

			{/*
			 * capture="environment" hints tablets/phones to open the rear camera.
			 * On desktop it degrades to a normal file picker.
			 */}
			<input
				ref={inputRef}
				type="file"
				accept="image/*"
				capture="environment"
				hidden
				onChange={handleFile}
			/>

			{previewUrl ? (
				<Box
					sx={{
						position: "relative",
						borderRadius: 2,
						overflow: "hidden",
						border: "1px solid",
						borderColor: "divider",
					}}
				>
					<Box
						component="img"
						src={previewUrl}
						alt={label}
						sx={{
							display: "block",
							width: "100%",
							maxHeight: { xs: 200, md: 240 },
							objectFit: "contain",
							backgroundColor: "#000",
						}}
					/>
					{!disabled && (
						<>
							<Button
								size="large"
								variant="contained"
								color="inherit"
								startIcon={<ReplayIcon />}
								onClick={openCamera}
								sx={{
									position: "absolute",
									bottom: 8,
									right: 8,
									backgroundColor: "rgba(255,255,255,0.9)",
								}}
							>
								Retake
							</Button>
							<IconButton
								aria-label="Remove photo"
								onClick={() => onChange(null)}
								sx={{
									position: "absolute",
									top: 8,
									right: 8,
									backgroundColor: "rgba(255,255,255,0.9)",
									"&:hover": { backgroundColor: "rgba(255,255,255,1)" },
								}}
							>
								<CloseIcon />
							</IconButton>
						</>
					)}
				</Box>
			) : disabled ? (
				<Box
					sx={{
						width: "100%",
						minHeight: { xs: 140, md: 170 },
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						gap: 1,
						borderRadius: 2,
						border: "2px dashed",
						borderColor: "divider",
						color: "text.disabled",
						backgroundColor: "action.hover",
					}}
				>
					<NoPhotographyIcon sx={{ fontSize: 48 }} />
					<Typography variant="body1" fontWeight={500}>
						No photo uploaded
					</Typography>
				</Box>
			) : (
				<ButtonBase
					onClick={openCamera}
					sx={{
						width: "100%",
						minHeight: { xs: 140, md: 170 },
						flexDirection: "column",
						gap: 1,
						borderRadius: 2,
						border: "2px dashed",
						borderColor: "divider",
						color: "text.secondary",
						backgroundColor: "action.hover",
						transition: "border-color .15s",
						"&:hover": { borderColor: "primary.main" },
					}}
				>
					<PhotoCameraIcon sx={{ fontSize: 48 }} />
					<Typography variant="body1" fontWeight={500}>
						Tap to capture
					</Typography>
				</ButtonBase>
			)}
		</Box>
	);
};
